/**
 * Homepage Data Hook - React Query data fetching
 *
 * Pure data fetching hook that queries the homepage configuration.
 * Use useHomepageState for the composition hook with transformations.
 */
import { useQuery } from '@tanstack/react-query'

import { homepageApi } from '../../lib'
import { homepageKeys } from '../queryKeys'

import type { HomepageConfigResponse } from '../../types'

export interface UseHomepageDataOptions {
  /** Store ID for multi-store support (optional) */
  storeId?: number
}

/**
 * Raw data fetching hook for homepage configuration
 *
 * Returns the raw React Query result without transformations.
 * For transformed data, use useHomepageState instead.
 *
 * @example
 * const { data, isLoading, error } = useHomepageData()
 */
export const useHomepageData = (options: UseHomepageDataOptions = {}) => {
  const { storeId } = options

  return useQuery<HomepageConfigResponse>({
    queryKey: homepageKeys.config(storeId),
    queryFn: () => homepageApi.getConfig(storeId),
  })
}

